import { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow, RevealImage } from "./Reveal";

const CASES = [
  {
    id: 0,
    title: "Case 01",
    subtitle: "Hydration & revitalisation",
    sessions: "Course per professional assessment",
  },
  {
    id: 1,
    title: "Case 02",
    subtitle: "Lip quality & smoothness",
    sessions: "3 sessions · approx. 2 weeks apart",
  },
  {
    id: 2,
    title: "Case 03",
    subtitle: "Mature lip revitalisation",
    sessions: "4 sessions · per professional recommendation",
  },
  {
    id: 3,
    title: "Case 04",
    subtitle: "Fresh, healthy-looking glow",
    sessions: "Immediately after a session",
  },
  {
    id: 4,
    title: "Case 05",
    subtitle: "Fuller-looking, conditioned lips",
    sessions: "3 sessions · approx. 2 weeks apart",
  },
  {
    id: 5,
    title: "Case 06",
    subtitle: "Natural lip revitalisation",
    sessions: "Course per professional assessment",
  },
  {
    id: 6,
    title: "Case 07",
    subtitle: "Hydrated, fresher look",
    sessions: "Course per professional assessment",
  },
  {
    id: 7,
    title: "Case 08",
    subtitle: "Soft, healthy-looking lips",
    sessions: "Course per professional assessment",
  },
];

const PlaceholderPane = ({ label, tone, caseId }) => (
  <div
    className={`absolute inset-0 flex flex-col items-center justify-center text-center p-6 ${
      tone === "before" ? "bg-[#E8DED8]" : "bg-[#EFD3DC]"
    }`}
  >
    <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#A31D4F]">{label}</p>
    <p className="font-serif italic text-lg sm:text-2xl text-[#1C1417]/80 mt-3 max-w-[260px]">
      Real Skinfill Bacio photo pending upload
    </p>
    <p className="text-[11px] text-[#6E6266] mt-2 font-mono">
      /public/assets/{label.toLowerCase()}-{caseId + 1}.webp
    </p>
  </div>
);

const CaseImage = ({ src, alt, fallback }) => {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setOk(false)}
      className="absolute inset-0 w-full h-full object-cover"
      draggable={false}
    />
  ) : (
    fallback
  );
};

const CompareSlider = ({ caseData }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider-container"
      className="relative w-full aspect-[4/3] sm:aspect-[16/9] rounded-[28px] sm:rounded-[36px] overflow-hidden select-none touch-none cursor-ew-resize shadow-[0_60px_120px_-50px_rgba(163,29,79,0.35)]"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <CaseImage
        src={`/assets/before-${caseData.id + 1}.webp`}
        alt={`Before — Skinfill Bacio lip booster result, ${caseData.subtitle.toLowerCase()}`}
        fallback={<PlaceholderPane label="BEFORE" tone="before" caseId={caseData.id} />}
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <CaseImage
          src={`/assets/after-${caseData.id + 1}.webp`}
          alt={`After — Skinfill Bacio lip booster result, ${caseData.subtitle.toLowerCase()}`}
          fallback={<PlaceholderPane label="AFTER" tone="after" caseId={caseData.id} />}
        />
      </div>

      <span className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#1C1417]/70 bg-white/40 backdrop-blur-md rounded-full px-4 py-1.5">
        Before
      </span>
      <span className="absolute top-5 right-5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-[#1C1417]/35 backdrop-blur-md rounded-full px-4 py-1.5">
        After
      </span>

      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-px bg-white/90 shadow-[0_0_14px_rgba(0,0,0,0.4)]" />
        <div
          data-testid="before-after-drag-handle"
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/75 backdrop-blur-md border border-white shadow-2xl flex items-center justify-center"
        >
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true">
            <path d="M5 1L1 6l4 5M11 1l4 5-4 5" stroke="#A31D4F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
};

const BeforeAfter = () => {
  const [active, setActive] = useState(0);
  const caseData = CASES[active];

  return (
    <section id="before-after" data-testid="before-after-section" className="bg-[#FAF7F5] py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <Reveal>
              <Eyebrow>Results</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif font-light text-[#1C1417] text-4xl sm:text-6xl tracking-tight mt-5 text-balance leading-[1.02]">
                Real lips.
                <br />
                <span className="italic text-[#A31D4F]">Beautifully refreshed.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.14}>
            <div className="flex flex-wrap gap-x-6 gap-y-3 sm:gap-x-8">
              {CASES.map((c, i) => (
                <button
                  key={c.id}
                  data-testid={`before-after-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`group relative font-mono text-[11px] sm:text-xs tracking-[0.2em] pb-2 transition-colors duration-300 ${
                    active === i ? "text-[#A31D4F]" : "text-[#6E6266] hover:text-[#1C1417]"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                  <span
                    className={`absolute bottom-0 left-0 h-px bg-[#A31D4F] transition-all duration-500 ${
                      active === i ? "w-full" : "w-0 group-hover:w-1/2"
                    }`}
                  />
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <RevealImage delay={0.15} className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <CompareSlider caseData={caseData} />
            </motion.div>
          </AnimatePresence>
        </RevealImage>

        <Reveal delay={0.2}>
          <div className="mt-7 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
            <p className="font-serif text-xl sm:text-2xl text-[#1C1417]">
              {caseData.title} — <span className="italic text-[#A31D4F]">{caseData.subtitle}</span>
            </p>
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#6E6266]">
              {caseData.sessions}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.26}>
          <p className="mt-10 text-[11px] sm:text-xs text-[#6E6266] font-light leading-relaxed max-w-2xl">
            Individual results may vary. Images are shown for illustrative and educational purposes only. Suitability
            is determined during a consultation with a qualified professional.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default BeforeAfter;
